import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import contractUtils, { switchToBaseMainnet, formatAddress } from '../utils/contractUtils';
import SimpleHeader from '../components/SimpleHeader';
import RoleStatus from '../components/RoleStatus';
import { useTheme } from '../contexts/ThemeContext';

// Add custom CSS animation for the spinners using component state and inline styles
// The spinning animation will be applied using className or inlined as needed

// Component styling function that adapts to theme
const getStyles = (isDark) => ({
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  section: {
    marginBottom: '30px',
    padding: '20px',
    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f9f9f9',
    color: isDark ? '#f7f9fb' : '#1f2937',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  label: {
    fontWeight: 'bold',
  },
  input: {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  select: {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  button: {
    padding: '12px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  disabledButton: {
    padding: '12px',
    backgroundColor: '#cccccc',
    color: '#666666',
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontWeight: 'bold',
  },
  connectButton: {
    padding: '12px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    marginTop: '10px',
  },
  error: {
    color: 'red',
    padding: '10px',
    backgroundColor: '#ffeeee',
    borderRadius: '4px',
    marginTop: '15px',
  },
  success: {
    color: 'green',
    padding: '10px',
    backgroundColor: '#eeffee',
    borderRadius: '4px',
    marginTop: '15px',
  },
  infoBox: {
    backgroundColor: isDark ? 'rgba(55, 91, 210, 0.15)' : '#e7f5fe',
    color: isDark ? '#4dc7e4' : '#0c4a6e',
    padding: '15px',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  warningBox: {
    backgroundColor: isDark ? 'rgba(251, 191, 36, 0.15)' : '#fff3e6',
    color: isDark ? '#fbbf24' : '#7c2d12',
    padding: '15px',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '5px 10px',
    borderRadius: '15px',
    fontWeight: 'bold',
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
  },
});

// CSS animations defined inline where needed

// Define CSS for animations
const spinAnimation = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@-webkit-keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

function CaseRegistrationPage() {
  // Get theme context
  const { isDark } = useTheme();

  // Get theme-aware styles
  const styles = getStyles(isDark);

  // Inject global CSS for animations
  useEffect(() => {
    // Create style element for animations
    const styleElement = document.createElement('style');
    styleElement.innerText = spinAnimation;
    document.head.appendChild(styleElement);

    // Clean up style element on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  // Wallet connection state
  const [account, setAccount] = useState('');
  const [isRegistrar, setIsRegistrar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [networkName, setNetworkName] = useState('');
  const [chainId, setChainId] = useState(null);
  
  // Rate limiting for refresh
  const [nextRefreshAllowed, setNextRefreshAllowed] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0); // For countdown display
  const REFRESH_COOLDOWN_BASE = 2000; // 2 seconds base cooldown 
  const MAX_REFRESH_COUNT = 5; // After this many refreshes, longer cooldown applies
  const EXTENDED_COOLDOWN = 10000; // 10 seconds after too many refreshes
  
  // Timer to update the cooldown display
  useEffect(() => {
    let timer = null;
    
    if (nextRefreshAllowed > Date.now()) {
      // Start a timer that updates every 100ms
      timer = setInterval(() => {
        const remaining = Math.max(0, nextRefreshAllowed - Date.now());
        setTimeRemaining(remaining);
        
        // Clear the timer when cooldown is complete
        if (remaining === 0) {
          clearInterval(timer);
        }
      }, 100); // Update frequently for smooth countdown
    }
    
    // Clean up on unmount or when dependencies change
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [nextRefreshAllowed]);
  
  // Form state
  const [ipfsUri, setIpfsUri] = useState('');
  const [status, setStatus] = useState('draft');
  const [defendant, setDefendant] = useState('0x0000000000000000000000000000000000000000');
  const [filingFee, setFilingFee] = useState('');
  
  // Results state
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [registeredCases, setRegisteredCases] = useState([]);
  
  // Connect wallet effect
  useEffect(() => {
    const connectWallet = async () => {
      try {
        if (window.ethereum) {
          // Set up listeners for account and chain changes
          window.ethereum.on('accountsChanged', handleAccountsChanged);
          window.ethereum.on('chainChanged', handleChainChanged);
          
          await handleAccountsChanged();
          await handleChainChanged();
        }
      } catch (err) {
        console.error('Error in useEffect:', err);
        setError(`Wallet connection error: ${err.message}`);
      }
    };
    
    connectWallet();
    
    // Cleanup listeners
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);
  
  // Get contract data when account changes
  useEffect(() => {
    const updateContractData = async () => {
      try {
        if (account && chainId === 8453) {
          console.log(`Updating contract data for account ${account} on chain ${chainId}`);
          
          // Get contract instance directly without storing the provider
          const contract = contractUtils.getContract();
          
          // Get filing fee
          const fee = await contract.filingFee();
          setFilingFee(ethers.formatEther(fee));
          console.log(`Filing fee: ${ethers.formatEther(fee)} ETH`);
          
          // Check if account is registrar
          console.log(`Checking if account ${account} has REGISTRAR_ROLE...`);
          const hasRegistrarRole = await contractUtils.hasRole(account, 'REGISTRAR_ROLE');
          console.log(`REGISTRAR_ROLE check result: ${hasRegistrarRole}`);
          setIsRegistrar(hasRegistrarRole);
          
          // Display expected registrars from contract data
          import('../contracts/SmallClaimsCaseRegistry.json').then(contractDataFile => {
            console.log('Expected registrars from contract data:', contractDataFile.registrars);
            
            // Check if account is in the list of expected registrars
            const isInList = contractDataFile.registrars.some(
              registrar => registrar.toLowerCase() === account.toLowerCase()
            );
            console.log(`Account ${account} is in registrars list: ${isInList}`);
          });
          
          // Check for existing cases using a safer approach
          let casesFound = [];
          try {
            // Try to fetch multiple case IDs in parallel
            const casePromises = Array.from({ length: 5 }, (_, i) => {
              return contractUtils.getCaseDetails(i)
                .then(details => {
                  console.log(`Found case ${i}:`, details);
                  return details;
                })
                .catch(err => {
                  // Silently ignore expected errors for non-existent cases
                  console.log(`Case ${i} not found or other error:`, err.message);
                  return null;
                });
            });
            
            // Wait for all promises to resolve
            const results = await Promise.all(casePromises);
            
            // Filter out nulls (cases that don't exist)
            casesFound = results.filter(result => result !== null);
            
            if (casesFound.length > 0) {
              console.log(`Found ${casesFound.length} cases`);
              setRegisteredCases(casesFound);
            } else {
              console.log('No existing cases found');
            }
          } catch (err) {
            console.log('Error checking for cases:', err);
          }
        }
      } catch (err) {
        console.error('Error updating contract data:', err);
        // Show a more user-friendly error message that doesn't expose internal details
        setError("Unable to load contract data. Please check your connection and try again.");
        
        // Log the full error for debugging
        console.error('Full error details:', {
          message: err.message,
          code: err.code,
          reason: err.reason,
          stack: err.stack
        });
      }
    };
    
    updateContractData();
  }, [account, chainId]);
  
  const handleAccountsChanged = async () => {
    try {
      if (window.ethereum) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.send("eth_requestAccounts", []);
        
        if (accounts.length > 0) {
          setAccount(accounts[0]);
        } else {
          setAccount('');
          setIsRegistrar(false);
        }
      }
    } catch (err) {
      console.error('Error in handleAccountsChanged:', err);
      setError(`Account connection error: ${err.message}`);
    }
  };
  
  const handleChainChanged = async () => {
    try {
      if (window.ethereum) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const network = await provider.getNetwork();
        setChainId(Number(network.chainId));
        setNetworkName(network.name);
      }
    } catch (err) {
      console.error('Error in handleChainChanged:', err);
    }
  };
  
  const handleConnectWallet = async () => {
    try {
      if (window.ethereum) {
        await handleAccountsChanged();
        
        // Use switchToBaseMainnet from contractUtils
        if (chainId !== 8453) {
          try {
            await switchToBaseMainnet();
          } catch (switchError) {
            console.error('Error switching to Base network:', switchError);
            setError(`Could not switch to Base network: ${switchError.message}`);
          }
        }
      } else {
        setError('MetaMask is not installed. Please install MetaMask or another compatible wallet.');
      }
    } catch (err) {
      console.error('Error connecting wallet:', err);
      setError(`Failed to connect wallet: ${err.message}`);
    }
  };
  
  const handleRegisterCase = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Form validation
      if (!ipfsUri) {
        throw new Error('IPFS URI is required');
      }
      
      if (!ethers.isAddress(defendant)) {
        throw new Error('Invalid defendant address format');
      }
      
      console.log('Attempting to register case on blockchain with the following parameters:');
      console.log('Contract address:', contractUtils.getContract().target);
      console.log('IPFS URI:', ipfsUri);
      console.log('Status:', status);
      console.log('Plaintiff:', account);
      console.log('Defendant:', defendant);
      console.log('Filing Fee:', filingFee, 'ETH');
      
      // Register case using the contract utils function
      const result = await contractUtils.registerCase(
        account,
        ipfsUri,
        status,
        defendant
      );
      
      console.log('Registration result:', result);
      
      // Add the new case to our list
      const newCase = {
        caseId: result.tokenId,
        uri: ipfsUri,
        status: status,
        plaintiff: account,
        defendant: defendant,
        owner: account,
        transactionHash: result.transactionHash
      };
      
      setRegisteredCases([...registeredCases, newCase]);
      setSuccessMessage(`Case registered successfully! Case ID: ${result.tokenId}, Transaction Hash: ${result.transactionHash}`);
      
      // Reset form
      setIpfsUri('');
      setStatus('draft');
      setDefendant('0x0000000000000000000000000000000000000000');
      
    } catch (err) {
      console.error('Error registering case:', err);
      setError(`Error registering case: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Wallet not connected view
  if (!account) {
    return (
      <div>
        <SimpleHeader />
        <div style={{...styles.container, marginTop: '20px'}}>
          <div style={styles.header}>
            <h1>Civil Claims Case Registry</h1>
            <h2>Base Blockchain Demo</h2>
          </div>
          
          <div style={styles.section}>
            <h2>Connect Wallet</h2>
            <p>Please connect your wallet to interact with the Civil Claims Case Registry.</p>
            
            {error && <div style={styles.error}>{error}</div>}
            
            <button 
              style={styles.connectButton}
              onClick={handleConnectWallet}
            >
              Connect Wallet
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Wrong network view
  if (chainId !== 8453) {
    return (
      <div>
        <SimpleHeader />
        <div style={{...styles.container, marginTop: '20px'}}>
          <div style={styles.header}>
            <h1>Civil Claims Case Registry</h1>
            <h2>Base Blockchain Demo</h2>
          </div>
          
          <div style={styles.section}>
            <h2>Wrong Network</h2>
            <div style={styles.warningBox}>
              <p><strong>You are connected to {networkName || 'an unsupported network'} (Chain ID: {chainId}).</strong></p>
              <p>This application requires the Base Mainnet (Chain ID: 8453).</p>
            </div>
            
            {error && <div style={styles.error}>{error}</div>}
            
            <button 
              style={styles.connectButton}
              onClick={handleConnectWallet}
            >
              Switch to Base Network
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Function to refresh cases with rate limiting
  const refreshCases = async () => {
    // Get current refresh status directly 
    const currentRefreshStatus = timeRemaining > 0
      ? { allowed: false, cooldownSeconds: Math.ceil(timeRemaining / 1000) }
      : { allowed: true, cooldownSeconds: 0 };
      
    // Check if refresh is allowed
    if (!currentRefreshStatus.allowed) {
      const seconds = currentRefreshStatus.cooldownSeconds;
      setError(`Please wait ${seconds} second${seconds !== 1 ? 's' : ''} before refreshing again. Blockchain queries need time to process.`);
      return;
    }
    
    // Update refresh count and calculate next allowed refresh time
    const now = Date.now();
    const newRefreshCount = refreshCount + 1;
    setRefreshCount(newRefreshCount);
    
    // Set longer cooldown if user has refreshed many times
    let cooldownTime = REFRESH_COOLDOWN_BASE;
    if (newRefreshCount > MAX_REFRESH_COUNT) {
      cooldownTime = EXTENDED_COOLDOWN;
      console.log("Too many refreshes, applying extended cooldown");
    }
    
    const nextAllowed = now + cooldownTime;
    setNextRefreshAllowed(nextAllowed);
    setTimeRemaining(cooldownTime); // Initialize the time remaining
    
    // Now proceed with the actual refresh
    setLoading(true);
    setError('');
    
    try {
      // Inform user that blockchain queries may take time
      setTimeout(() => {
        if (loading) {
          console.log("Still loading after 3s, showing user feedback message");
          setError("Blockchain queries may take some time. Please be patient...");
        }
      }, 3000);
      
      // Try to fetch cases with a more comprehensive approach - check more cases (up to 20)
      // Process in batches to avoid overwhelming the blockchain node
      const BATCH_SIZE = 5;
      let casesFound = [];
      
      for (let batch = 0; batch < 4; batch++) { // 4 batches of 5 = 20 cases
        const startId = batch * BATCH_SIZE;
        const batchPromises = Array.from({ length: BATCH_SIZE }, (_, i) => {
          const caseId = startId + i;
          return contractUtils.getCaseDetails(caseId)
            .then(details => {
              console.log(`Found case ${caseId}:`, details);
              return details;
            })
            .catch(err => {
              // Silently ignore expected errors
              return null;
            });
        });
        
        const batchResults = await Promise.all(batchPromises);
        const validResults = batchResults.filter(result => result !== null);
        casesFound = [...casesFound, ...validResults];
        
        // If batch is empty, we can probably stop querying
        if (validResults.length === 0 && batch > 0) {
          console.log(`Batch ${batch} returned no results, stopping further queries`);
          break;
        }
      }
      
      if (casesFound.length > 0) {
        console.log(`Found ${casesFound.length} cases`);
        setRegisteredCases(casesFound);
        // Reset error if previously shown
        setError('');
      } else {
        console.log('No existing cases found');
        setRegisteredCases([]);
      }
    } catch (err) {
      console.error('Error refreshing cases:', err);
      setError("Unable to refresh cases. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Not a registrar view
  if (!isRegistrar) {
    return (
      <div>
        <SimpleHeader />
        <div style={{...styles.container, marginTop: '20px'}}>
          <div style={styles.header}>
            <h1>Civil Claims Case Registry</h1>
            <h2>Base Blockchain Demo</h2>
          </div>
          
          <div style={styles.section}>
            <h2>Permission Verification</h2>
            
            {/* Role Status Component - detailed blockchain verification */}
            <RoleStatus address={account} />
            
            <div style={{...styles.infoBox, marginTop: '20px'}}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>View Existing Cases</h3>
                <button 
                  onClick={refreshCases}
                  disabled={loading || timeRemaining > 0}
                  title={timeRemaining > 0 ? `Please wait ${Math.ceil(timeRemaining/1000)} seconds` : "Refresh cases from blockchain"}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: loading || timeRemaining > 0 ? '#a0aec0' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading || timeRemaining > 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    transition: 'background-color 0.3s'
                  }}
                >
                  {loading ? (
                    <>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid rgba(255,255,255,0.3)', 
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        WebkitAnimation: 'spin 1s linear infinite',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Loading...
                    </>
                  ) : timeRemaining > 0 ? (
                    <>
                      <span style={{ fontSize: '16px' }}>⏱</span>
                      Wait {Math.ceil(timeRemaining/1000)}s
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '16px' }}>↻</span>
                      Refresh Cases
                    </>
                  )}
                </button>
              </div>
              <p>You can view existing case information below:</p>
            </div>
            
            {loading && registeredCases.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ 
                  display: 'inline-block', 
                  width: '40px', 
                  height: '40px', 
                  border: '3px solid rgba(0,0,0,0.1)', 
                  borderTopColor: '#3498db',
                  borderRadius: '50%',
                  WebkitAnimation: 'spin 1s linear infinite',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p>Loading cases from blockchain...</p>
              </div>
            ) : registeredCases.length > 0 ? (
              <div>
                <h3>Registered Cases</h3>
                {registeredCases.map(caseData => (
                  <div key={caseData.caseId} style={{...styles.section, marginTop: '10px'}}>
                    <h4>Case ID: {caseData.caseId}</h4>
                    <p><strong>IPFS URI:</strong> {caseData.uri}</p>
                    <p><strong>Status:</strong> {caseData.status}</p>
                    <p><strong>Plaintiff:</strong> {formatAddress(caseData.plaintiff, 8)}</p>
                    <p><strong>Defendant:</strong> {
                      caseData.defendant === ethers.ZeroAddress ? 
                        "Not assigned yet" : 
                        formatAddress(caseData.defendant, 8)
                    }</p>
                    <p>
                      <strong>View on BlockExplorer:</strong> 
                      <a 
                        href={`https://basescan.org/token/0x4B2cF77Dbd063299536AB916Fb1445bD3267162F?a=${caseData.caseId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: '5px', color: '#3498db' }}
                      >
                        BaseScan
                      </a>
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <p style={{ fontSize: '16px', color: '#6c757d' }}>No cases have been registered yet.</p>
                <button 
                  onClick={refreshCases}
                  disabled={loading || timeRemaining > 0}
                  title={timeRemaining > 0 ? `Please wait ${Math.ceil(timeRemaining/1000)} seconds` : "Check for cases"}
                  style={{
                    marginTop: '15px',
                    padding: '8px 16px',
                    backgroundColor: loading || timeRemaining > 0 ? '#a0aec0' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading || timeRemaining > 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.3s'
                  }}
                >
                  {loading ? "Loading..." : 
                   timeRemaining > 0 ? `Wait ${Math.ceil(timeRemaining/1000)}s` : 
                   "Check Again"}
                </button>
              </div>
            )}
            
            {error && <div style={styles.error}>{error}</div>}
          </div>
        </div>
      </div>
    );
  }
  
  // Main view for registrars
  return (
    <div>
      <SimpleHeader />
      <div style={{...styles.container, marginTop: '20px'}}>
        <div style={styles.header}>
          <h1>Civil Claims Case Registry</h1>
          <h2>Base Blockchain Demo</h2>
        </div>
      
        <div style={styles.section}>
          <h2>Connected Account</h2>
          <p><strong>Network:</strong> {networkName} (Chain ID: {chainId})</p>
          
          {/* Role Status Component - dynamic permission display */}
          <RoleStatus address={account} />
        </div>
      
        <div style={styles.section}>
          <h2>Register New Case</h2>
          
          {error && <div style={styles.error}>{error}</div>}
          {successMessage && <div style={styles.success}>{successMessage}</div>}
          
          <form onSubmit={handleRegisterCase} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="ipfsUri">IPFS URI:</label>
              <input
                style={styles.input}
                type="text"
                id="ipfsUri"
                value={ipfsUri}
                onChange={(e) => setIpfsUri(e.target.value)}
                placeholder="ipfs://..."
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="status">Initial Status:</label>
              <select
                style={styles.select}
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="filed">Filed</option>
                <option value="pending">Pending</option>
                <option value="hearing_scheduled">Hearing Scheduled</option>
                <option value="judgment_entered">Judgment Entered</option>
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="defendant">Defendant Address (optional):</label>
              <input
                style={styles.input}
                type="text"
                id="defendant"
                value={defendant}
                onChange={(e) => setDefendant(e.target.value)}
                placeholder="0x..."
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Filing Fee:</label>
              <div>{filingFee} ETH</div>
            </div>
            
            <button 
              type="submit" 
              style={loading ? styles.disabledButton : styles.button}
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register Case'}
            </button>
          </form>
        </div>
        
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ margin: 0 }}>Registered Cases</h2>
            <button 
              onClick={refreshCases}
              disabled={loading || timeRemaining > 0}
              title={timeRemaining > 0 ? `Please wait ${Math.ceil(timeRemaining/1000)} seconds` : "Refresh cases from blockchain"}
              style={{
                padding: '8px 16px',
                backgroundColor: loading || timeRemaining > 0 ? '#a0aec0' : '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || timeRemaining > 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                transition: 'background-color 0.3s'
              }}
            >
              {loading ? (
                <>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid rgba(255,255,255,0.3)', 
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    WebkitAnimation: 'spin 1s linear infinite',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Loading...
                </>
              ) : timeRemaining > 0 ? (
                <>
                  <span style={{ fontSize: '16px' }}>⏱</span>
                  Wait {Math.ceil(timeRemaining/1000)}s
                </>
              ) : (
                <>
                  <span style={{ fontSize: '16px' }}>↻</span>
                  Refresh Cases
                </>
              )}
            </button>
          </div>
            
          {loading && registeredCases.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ 
                display: 'inline-block', 
                width: '40px', 
                height: '40px', 
                border: '3px solid rgba(0,0,0,0.1)', 
                borderTopColor: '#3498db',
                borderRadius: '50%',
                WebkitAnimation: 'spin 1s linear infinite',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p>Loading cases from blockchain...</p>
            </div>
          ) : registeredCases.length > 0 ? (
            <div> {/* Add container div to wrap the mapped elements */}
              {registeredCases.map(caseData => (
                <div key={caseData.caseId} style={{...styles.section, marginTop: '10px'}}>
                  <h4>Case ID: {caseData.caseId}</h4>
                  <p><strong>IPFS URI:</strong> {caseData.uri}</p>
                  <p><strong>Status:</strong> {caseData.status}</p>
                  <p><strong>Plaintiff:</strong> {formatAddress(caseData.plaintiff, 8)}</p>
                  <p><strong>Defendant:</strong> {
                    caseData.defendant === ethers.ZeroAddress ? 
                      "Not assigned yet" : 
                      formatAddress(caseData.defendant, 8)
                  }</p>
                  <p>
                    <strong>View on BlockExplorer:</strong> 
                    <a 
                      href={`https://basescan.org/token/0x4B2cF77Dbd063299536AB916Fb1445bD3267162F?a=${caseData.caseId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginLeft: '5px', color: '#3498db' }}
                    >
                      BaseScan
                    </a>
                  </p>
                  {caseData.transactionHash && (
                    <p>
                      <strong>Transaction:</strong> 
                      <a 
                        href={`https://basescan.org/tx/${caseData.transactionHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{marginLeft: '5px', color: '#3498db'}}
                      >
                        View Transaction
                      </a>
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <p style={{ fontSize: '16px', color: '#6c757d' }}>No cases have been registered yet.</p>
              <button 
                onClick={refreshCases}
                disabled={loading || timeRemaining > 0}
                title={timeRemaining > 0 ? `Please wait ${Math.ceil(timeRemaining/1000)} seconds` : "Check for cases"}
                style={{
                  marginTop: '15px',
                  padding: '8px 16px',
                  backgroundColor: loading || timeRemaining > 0 ? '#a0aec0' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || timeRemaining > 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.3s'
                }}
              >
                {loading ? "Loading..." : 
                 timeRemaining > 0 ? `Wait ${Math.ceil(timeRemaining/1000)}s` : 
                 "Check Again"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CaseRegistrationPage;